import React from 'react';

interface SlotInput {
  slot_id: number;
  slot_date: string;
  am_pm: 'AM' | 'PM';
  vote_count?: number;
  voters?: string[];
}

interface Props {
  slots: SlotInput[];
  selectedSlotIds: number[];
  onToggle: (slotId: number) => void;
  disabled?: boolean;
  showVoteCounts?: boolean;
  onDeleteSlot?: (slotId: number) => void;
}

function fmtDay(d: string) {
  const ds = d.length >= 10 ? d.slice(0, 10) : d;
  const m = ds.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return { dayName: d, dayShort: '' };
  const dt = new Date(+m[1], +m[2] - 1, +m[3]);
  if (isNaN(dt.getTime())) return { dayName: d, dayShort: '' };
  return {
    dayName: dt.toLocaleDateString('en-CA', { weekday: 'short' }),
    dayShort: dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }),
  };
}

/**
 * One card per day. Each card has the date label, plus AM and PM buttons
 * side-by-side. Cards wrap into a responsive grid — multiple per row on
 * desktop, one per row on mobile. No horizontal scrolling needed.
 */
const PollAvailabilityGrid: React.FC<Props> = ({
  slots, selectedSlotIds, onToggle, disabled, showVoteCounts = true, onDeleteSlot,
}) => {
  const byDate: Record<string, { AM?: SlotInput; PM?: SlotInput }> = {};
  for (const s of slots) {
    const key = s.slot_date.length >= 10 ? s.slot_date.slice(0, 10) : s.slot_date;
    if (!byDate[key]) byDate[key] = {};
    byDate[key][s.am_pm] = s;
  }
  const sortedDates = Object.keys(byDate).sort();

  if (sortedDates.length === 0) return <div style={styles.empty}>No time slots yet.</div>;

  const sessionButton = (s: SlotInput | undefined, label: 'AM' | 'PM') => {
    const isOn = !!(s && selectedSlotIds.includes(s.slot_id));
    if (!s) {
      return (
        <div style={{ ...styles.session, ...styles.sessionMissing }}>
          <div style={styles.sessionLabel}>{label === 'AM' ? 'Morning' : 'Afternoon'}</div>
          <div style={{ fontSize: 11, color: '#adb5bd' }}>not offered</div>
        </div>
      );
    }
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => onToggle(s.slot_id)}
        style={{
          ...styles.session,
          ...(isOn ? styles.sessionOn : {}),
          ...(disabled ? styles.sessionDisabled : {}),
        }}
      >
        <div style={{ ...styles.sessionLabel, ...(isOn ? { color: '#fff' } : {}) }}>
          {label === 'AM' ? 'Morning' : 'Afternoon'}
        </div>
        <div style={{ ...styles.check, ...(isOn ? styles.checkOn : {}) }}>{isOn ? '✓' : ''}</div>
        {showVoteCounts && (
          <>
            <div style={{ ...styles.voteCount, ...(isOn ? { color: '#fff', opacity: 0.9 } : {}) }}>
              {s.vote_count ?? 0} vote{(s.vote_count ?? 0) === 1 ? '' : 's'}
            </div>
            {s.voters && s.voters.length > 0 && (
              <div style={{ ...styles.voterNames, ...(isOn ? { color: 'rgba(255,255,255,0.85)' } : {}) }}>
                {s.voters.map((name) => name.split(' ')[0]).join(', ')}
              </div>
            )}
          </>
        )}
      </button>
    );
  };

  return (
    <div style={styles.grid}>
      {sortedDates.map((d) => {
        const day = fmtDay(d);
        const am = byDate[d].AM;
        const pm = byDate[d].PM;
        return (
          <div key={d} style={styles.dayCard}>
            <div style={styles.dayHeader}>
              <div>
                <div style={styles.dayName}>{day.dayName}</div>
                <div style={styles.dayShort}>{day.dayShort}</div>
              </div>
              {onDeleteSlot && (am || pm) && (
                <button type="button" title="Delete both AM and PM for this day" style={styles.deleteBtn}
                  onClick={() => { if (am) onDeleteSlot(am.slot_id); if (pm) onDeleteSlot(pm.slot_id); }}>
                  ✕
                </button>
              )}
            </div>
            <div style={styles.sessionRow}>
              {sessionButton(am, 'AM')}
              {sessionButton(pm, 'PM')}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 10,
    padding: 8,
    border: '1px solid #dee2e6',
    borderRadius: 8,
    background: '#fff',
  },
  dayCard: {
    border: '1px solid #e9ecef',
    borderRadius: 8,
    background: '#fafafa',
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  dayHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: 4,
    borderBottom: '1px solid #e9ecef',
  },
  dayName: { textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.6, color: '#6c757d', fontWeight: 700 },
  dayShort: { fontSize: 15, fontWeight: 700, color: '#212529', marginTop: 1 },
  deleteBtn: { background: 'transparent', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: 14, padding: 2, lineHeight: 1 },
  sessionRow: { display: 'flex', gap: 6 },
  session: {
    flex: 1,
    minHeight: 72,
    border: '1px solid #dee2e6',
    borderRadius: 6,
    background: '#fff',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: '6px 4px',
    transition: 'all 0.12s ease',
    boxSizing: 'border-box',
  },
  sessionLabel: { fontSize: 11, fontWeight: 700, color: '#495057', letterSpacing: 0.3 },
  sessionOn: { background: '#0d6efd', borderColor: '#0d6efd', color: '#fff', boxShadow: '0 1px 3px rgba(13,110,253,0.3)' },
  sessionMissing: { background: '#f8f9fa', cursor: 'default', justifyContent: 'center' },
  sessionDisabled: { opacity: 0.55, cursor: 'not-allowed' },
  check: {
    width: 22, height: 22, borderRadius: 4,
    border: '2px solid #adb5bd', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    color: '#0d6efd', fontSize: 14, fontWeight: 700, background: '#fff',
  },
  checkOn: { background: '#fff', borderColor: '#fff' },
  voteCount: { fontSize: 11, color: '#6c757d', fontWeight: 500 },
  voterNames: { fontSize: 10, color: '#868e96', fontStyle: 'italic', textAlign: 'center' as const, lineHeight: 1.3 },
  empty: { color: '#888', padding: 16, fontSize: 13, textAlign: 'center' },
};

export default PollAvailabilityGrid;
